import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetBillingCharges } from "../../../../api/masters/billingChargesAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetIpdBillCharges, StoreIpdBillCharge } from "../../../../api/ipd/ipdBillChargesAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema (basic example — you can expand this)
const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD Entry is required"),
    billingCharges: yup.array().of(
        yup.object().shape({
            date: yup.date().required("Date is required"),
            billingChargesId: yup.string().required("Billing Charge is required"),
            doctorId: yup.string().required("Doctor Name is required"),
            unit: yup.number().required("Unit is required"),
            rate: yup.number().required("Rate is required"),
            amount: yup.number().required("Amount is required"),
            discount: yup.string().nullable(),
            vNo: yup.string().nullable(),
        })
    ),
    totalAmount: yup
        .number()
        .typeError("total Amount must be a number")
        .required("total Amount is required"),
    netAmount: yup
        .number()
        .typeError("Net Amount must be a number")
        .required("Net Amount is required")
})

const AddIpdBillChargesEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [ipdEntries, setIpdEntries] = useState([]);
    const [billingCharges, setBillingCharges] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [previousBillChargeEntry, setPreviousBillChargeEntry] = useState({});

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        control,
        watch,
        formState: { errors } } = useForm({
            resolver: yupResolver(schema),
            defaultValues: {
                billingCharges: [
                    {
                        billingChargesId: "",
                        doctorId: "",
                        unit: 1,
                        rate: 0,
                        amount: 0,
                        refundAmount: 0,
                    }
                ]
            }
        });

    const {
        fields: billingChargesFields,
        append: appendBillingCharges,
        remove: removeBillingCharges,
        replace: replaceBillingCharges
    } = useFieldArray({
        control,
        name: "billingCharges"
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchBillingCharges = async () => {
        try {
            const response = await GetBillingCharges();
            if (response.success) {
                setBillingCharges(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await GetConsultants();
            if (response.success) {
                setDoctors(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    useEffect(() => {
        fetchIpdEntries();
        fetchBillingCharges();
        fetchDoctors();
    }, []);

    const fetchPreviousEntry = async (ipdEntryId) => {
        try {
            const response = await GetIpdBillCharges({ ipdEntryId });

            if (response.success && response.data?.length > 0) {
                const lastEntry = response.data[0];

                setPreviousBillChargeEntry(lastEntry);

                const lastEntryBillingCharges = lastEntry.billingCharges.map((bc) => ({
                    date: bc.date?.split("T")[0],
                    billingChargesId: bc.billingChargesId?._id,
                    doctorId: bc.doctorId?._id,
                    unit: bc.unit,
                    rate: bc.rate,
                    amount: bc.amount,
                    discount: bc.discount,
                    vNo: bc.vNo,
                    isOld: true   // ✅ mark as old row
                }));

                // ✅ Set rows in field array
                replaceBillingCharges(
                    lastEntryBillingCharges.length > 0
                        ? lastEntryBillingCharges
                        : [{
                            date: new Date().toISOString().split("T")[0],
                            billingChargesId: "",
                            doctorId: "",
                            unit: 1,
                            rate: 0,
                            amount: 0,
                            discount: "No",
                            vNo: "",
                            isOld: false   // ✅ new row
                        }]
                );

                // ✅ Set other form fields
                setValue("totalAmount", lastEntry.totalAmount || 0);
                setValue("advance", lastEntry.advance || 0);
                setValue("toReference", lastEntry.toReference || 0);
                setValue("discountPercentage", lastEntry.discountPercentage || 0);
                setValue("discountAmount", lastEntry.discountAmount || 0);
                setValue("netAmount", lastEntry.netAmount || 0);
                setValue("balance", lastEntry.balance || 0);
                setValue("paymentMode", lastEntry.paymentMode || "");
                setValue("remark", lastEntry.remark || "");

            } else {
                // If no previous entry → reset to default one row
                replaceBillingCharges([{
                    date: new Date().toISOString().split("T")[0],
                    billingChargesId: "",
                    doctorId: "",
                    unit: 1,
                    rate: 0,
                    amount: 0,
                    discount: "No",
                    vNo: ""
                }]);
            }

        } catch (error) {
            showToast("error", error.message);
        }
    };


    const calculateAll = () => {
        const rows = getValues("billingCharges");

        // 1. Row amounts
        rows.forEach((row, index) => {
            const unit = parseFloat(row?.unit) || 0;
            const rate = parseFloat(row?.rate) || 0;
            const amount = unit * rate;

            if (row?.amount !== amount) {
                setValue(`billingCharges.${index}.amount`, amount, { shouldDirty: false });
            }
        });

        // 2. total Amount
        const total = rows.reduce(
            (sum, row) =>
                sum + (parseFloat(row?.amount) || 0) - (parseFloat(row?.refundAmount) || 0),
            0
        );

        if (getValues("totalAmount") !== total) {
            setValue("totalAmount", total, { shouldDirty: false });
        }

        const totalAfterRef = total - (parseFloat(getValues("toReference")) || 0);
        // 3. Discount
        const discountPercent = parseFloat(getValues("discountPercentage")) || 0;

        if (discountPercent > 0) {
            const discountAmount = (totalAfterRef * discountPercent) / 100;

            if (getValues("discountAmount") !== discountAmount) {
                setValue("discountAmount", discountAmount.toFixed(2), { shouldDirty: false });
            }
        }

        const finalDiscount = parseFloat(getValues("discountAmount")) || 0;

        const netAfterDiscount = totalAfterRef - finalDiscount;

        if (getValues("netAmount") !== netAfterDiscount) {
            setValue("netAmount", netAfterDiscount.toFixed(2), { shouldDirty: false });
        }
    };

    const billingRows = useWatch({ control, name: "billingCharges" });
    const discountPercentage = watch("discountPercentage");
    const discountAmount = watch("discountAmount"); // only if user edits manually
    const toReference = watch("toReference"); // only if user edits manually
    const totalAmount = watch("totalAmount");

    useEffect(() => {
        calculateAll();
    }, [billingRows, discountPercentage, discountAmount, toReference]);


    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });
            const response = await StoreIpdBillCharge(data);

            if (response.success) {
                showToast("success", response.message);
                // navigate(`/ipd/transaction/ipd-bill-charges-entry/list`);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="IPD Charges" title="Add IPD Charges Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Add IPD Charges Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/ipd-bill-charges-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List IPD Charges Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List IPD Charges Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Room No</label>
                                                <select
                                                    className="form-control"
                                                    {...register("ipdEntryId", {
                                                        onChange: (e) => {
                                                            setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                            setValue('advance', ipdEntries.find(ipd => ipd._id === e.target.value)?.advance || 0);
                                                            fetchPreviousEntry(e.target.value);
                                                        }
                                                    })}
                                                >
                                                    <option value="">Select Room No</option>
                                                    {ipdEntries.map(ipd =>
                                                        <option key={ipd._id} value={ipd._id}>
                                                            {ipd.roomId?.roomNo}
                                                        </option>
                                                    )}
                                                </select>
                                                {errors.ipdEntryId && (
                                                    <span className="text-danger">
                                                        {errors.ipdEntryId.message}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Billing Charges
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-12 my-3">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Billing Charges</th>
                                                        <th>Doctor Name</th>
                                                        <th>Unit</th>
                                                        <th>Rate</th>
                                                        <th>Amount</th>
                                                        <th>Disocunt</th>
                                                        <th>V.No</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {billingChargesFields.map((field, index) => {
                                                        const isOldRow = billingRows?.[index]?.isOld;

                                                        return <tr key={field.id} className={isOldRow ? "table-secondary" : ""}>
                                                            <td>
                                                                <input
                                                                    className="form-control"
                                                                    type="date"
                                                                    defaultValue={new Date().toISOString().slice(0, 10)}
                                                                    {...register(`billingCharges.${index}.date`)}
                                                                    placeholder="Date"
                                                                    readOnly={isOldRow}
                                                                />
                                                                {errors.billingCharges?.[index]?.date && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].date.message}
                                                                    </p>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-control"
                                                                    readOnly={isOldRow}
                                                                    {...register(`billingCharges.${index}.billingChargesId`,
                                                                        {
                                                                            onChange: (e) => {
                                                                                const selectedCharge = billingCharges.find(
                                                                                    (c) => c._id === e.target.value
                                                                                );

                                                                                if (selectedCharge) {
                                                                                    // Set rate from master
                                                                                    setValue(
                                                                                        `billingCharges.${index}.rate`,
                                                                                        selectedCharge.rate || 0
                                                                                    );

                                                                                    // Store rateEditable flag in form state
                                                                                    setValue(
                                                                                        `billingCharges.${index}.rateEditable`,
                                                                                        selectedCharge.rateEditable
                                                                                    );
                                                                                }
                                                                            }
                                                                        }
                                                                    )}
                                                                >
                                                                    <option value="">Select Charge Name</option>
                                                                    {billingCharges.map((charge) => (
                                                                        <option key={charge._id} value={charge._id}>
                                                                            {charge.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errors.billingCharges?.[index]?.billingChargesId && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].billingChargesId.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <select
                                                                    className="form-control"
                                                                    type="text"
                                                                    readOnly={isOldRow}
                                                                    {...register(`billingCharges.${index}.doctorId`)}
                                                                    placeholder="Doctor Name"
                                                                >
                                                                    <option value="">Select Doctor</option>
                                                                    {doctors.map((doctor) => (
                                                                        <option key={doctor._id} value={doctor._id}>
                                                                            {doctor.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errors.billingCharges?.[index]?.doctorId && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].doctorId.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    readOnly={isOldRow}
                                                                    {...register(`billingCharges.${index}.unit`)}
                                                                    placeholder="Unit"
                                                                />
                                                                {errors.billingCharges?.[index]?.unit && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].unit.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    {...register(`billingCharges.${index}.rate`)}
                                                                    placeholder="Rate"
                                                                    readOnly={!billingRows?.[index]?.rateEditable || isOldRow}
                                                                />
                                                                {errors.billingCharges?.[index]?.rate && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].rate.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    {...register(`billingCharges.${index}.amount`)}
                                                                    readOnly
                                                                    placeholder="Amount"
                                                                />
                                                                {errors.billingCharges?.[index]?.amount && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].amount.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <select
                                                                    className="form-control"
                                                                    readOnly={isOldRow}
                                                                    {...register(`billingCharges.${index}.discount`)}
                                                                >
                                                                    <option value="No">No</option>
                                                                    <option value="Yes">Yes</option>
                                                                </select>
                                                                {errors.billingCharges?.[index]?.discount && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].discount.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    className="form-control"
                                                                    type="number"
                                                                    readOnly={isOldRow}
                                                                    {...register(`billingCharges.${index}.vNo`)}
                                                                    placeholder="V No"
                                                                />
                                                                {errors.billingCharges?.[index]?.vNo && (
                                                                    <p className="text-danger">
                                                                        {errors.billingCharges[index].vNo.message}
                                                                    </p>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <div className="d-flex">
                                                                    {
                                                                        !isOldRow &&
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-danger me-2"
                                                                            onClick={() => billingChargesFields.length > 1 && removeBillingCharges(index)}
                                                                        >
                                                                            -
                                                                        </button>
                                                                    }
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={() =>
                                                                            appendBillingCharges({
                                                                                date: new Date().toISOString().slice(0, 10),
                                                                                billingChargesId: "",
                                                                                doctorId: "",
                                                                                unit: 1,
                                                                                rate: 0,
                                                                                amount: 0,
                                                                                discount: "No",
                                                                                vNo: "",
                                                                                isOld: false   // ✅ mark new row editable
                                                                            })
                                                                        }
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="row">

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Total Amount</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("totalAmount")}
                                                    placeholder="total Amount"
                                                    readOnly
                                                />
                                                {errors.totalAmount &&
                                                    <p className="text-danger">
                                                        {errors.totalAmount.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Advance</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("advance")}
                                                    placeholder="Advance"
                                                    readOnly
                                                />
                                                {errors.advance &&
                                                    <p className="text-danger">
                                                        {errors.advance.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>To Reference</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("toReference")}
                                                    placeholder="To Reference"
                                                />
                                                {errors.toReference &&
                                                    <p className="text-danger">
                                                        {errors.toReference.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discount Percentage</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-control"
                                                    {...register("discountPercentage", {
                                                        onChange: (e) => {
                                                            if (e.target.value > 100) {
                                                                e.target.value = 100;
                                                            }
                                                            const discountAmount = (e.target.value * totalAmount / 100).toFixed(2);
                                                            setValue("discountAmount", discountAmount);
                                                        }
                                                    })}
                                                    placeholder="Discount Percentage"
                                                />
                                                {errors.discountPercentage &&
                                                    <p className="text-danger">
                                                        {errors.discountPercentage.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Discount Amount</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-control"
                                                    {...register("discountAmount", {
                                                        onChange: (e) => {
                                                            const discountPercentage = (e.target.value * 100 / totalAmount).toFixed(2);
                                                            setValue("discountPercentage", discountPercentage);
                                                        }
                                                    })}
                                                    placeholder="Discount Amount"
                                                />
                                                {errors.discountAmount &&
                                                    <p className="text-danger">
                                                        {errors.discountAmount.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Net Amount</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("netAmount")}
                                                    placeholder="Net Amount"
                                                    readOnly
                                                />
                                                {errors.netAmount &&
                                                    <p className="text-danger">
                                                        {errors.netAmount.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Balance</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    {...register("balance")}
                                                    placeholder="Balance"
                                                    readOnly
                                                />
                                                {errors.balance &&
                                                    <p className="text-danger">
                                                        {errors.balance.message}
                                                    </p>}
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Payment Mode</label>
                                                <select
                                                    className="form-control"
                                                    {...register("paymentMode")}
                                                    placeholder="Payment Mode"
                                                >
                                                    <option value="">Select Payment Mode</option>
                                                    <option value="Cash">Cash</option>
                                                    <option value="Online">Online</option>
                                                </select>
                                                {errors.paymentMode &&
                                                    <p className="text-danger">
                                                        {errors.paymentMode.message}
                                                    </p>}
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-group">
                                                <label>Remark</label>
                                                <textarea
                                                    className="form-control"
                                                    {...register("remark")}
                                                    placeholder="Remark"
                                                />
                                                {errors.remark &&
                                                    <p className="text-danger">
                                                        {errors.remark.message}
                                                    </p>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer text-end">
                                    <button className="btn btn-primary me-2" type="submit">
                                        Submit
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddIpdBillChargesEntry;
